// In-buffer occurrences of the peeked identifier.
//
// Cross-object peek answers "where else in the DATABASE is this used?"; the
// active scratch buffer is part of the honest answer too. Rather than scanning
// text (NAV10 forbids regex identifier matching), this indexes the buffer with
// the SAME machinery the persisted-object index uses — `indexViewListing`
// parses a bare SELECT unchanged, because `stripCreateViewPrefix` is a no-op
// when there is no `CREATE VIEW … AS` header — and then queries it through
// `findReferences`, so the engine-aware identity comparator (#34) and the
// per-engine identifier folding apply exactly as they do for stored bodies.
//
// KNOWN LIMIT (inherited from the index): `indexViewListing` walks only
// `kind === 'select'` statements, so buffer occurrences inside INSERT /
// UPDATE / DELETE statements are NOT reported — peek on `orders` in
// `UPDATE orders SET …` shows the cross-object hits but omits the occurrence
// under the cursor, and a multi-statement buffer contributes only its SELECT
// statements. Lifting this means teaching the shared index builder about DML
// statement kinds, not special-casing here. Pinned by the non-SELECT cases in
// `bufferReferenceRanges.test.ts`.

import { createIdentifierIndex } from '../index/identifierIndex';
import { indexViewListing } from '../index/indexBuilder';
import type { SqlEngine } from '../../sql-grammar/model';
import type {
  IdentifierKind,
  PersistedObjectListing,
  QualifiedName,
  SqlRange,
} from '../types';

import { findReferences } from './findReferencesService';

/** Synthetic object identity for the scratch buffer. Never leaves this
 *  module — the throwaway index is discarded on return. */
const BUFFER_OBJECT_NAME: QualifiedName = {
  name: '__active_buffer__',
  raw: '__active_buffer__',
  quoted: false,
  foldedName: '__active_buffer__',
};

const BUFFER_CONNECTION_ID = '__active_buffer__';

export type BufferReferenceArgs = {
  readonly source: string;
  readonly engine: SqlEngine;
  readonly identifier: QualifiedName;
  readonly kind: IdentifierKind;
  readonly mysqlLowerCaseTableNames?: number;
};

/** Ranges (0-indexed, against `source`) where the identifier is referenced in
 *  the active buffer. Returns `[]` when the buffer does not parse — a scratch
 *  buffer mid-edit is the common case and must never break the peek. */
export function findBufferReferenceRanges(args: BufferReferenceArgs): readonly SqlRange[] {
  const index = createIdentifierIndex(BUFFER_CONNECTION_ID);
  const listing: PersistedObjectListing = {
    objectKind: 'view',
    objectName: BUFFER_OBJECT_NAME,
    bodyText: args.source,
    parseOptions: { engine: args.engine },
  };
  try {
    indexViewListing(listing, index);
  } catch {
    return [];
  }

  const result = findReferences(BUFFER_CONNECTION_ID, args.identifier, args.kind, {
    getIndex: () => index,
    engine: args.engine,
    mysqlLowerCaseTableNames: args.mysqlLowerCaseTableNames,
  });

  const ranges: SqlRange[] = [];
  for (const group of result.groups) {
    for (const ref of group.references) {
      // A `parse-failed` placeholder spans the whole buffer — surfacing it as
      // a "reference" would put a whole-document row in the peek list.
      if (ref.parseStatus !== 'ok') continue;
      ranges.push(ref.range);
    }
  }
  return ranges;
}
